import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {first} from 'rxjs/operators';
import {DatePipe} from '@angular/common';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {NotificationType} from '~core/enum';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {noWhitespaceCharacters} from '~shared/directives/form-validators';
import {AuthenticatedUser, Club, Customer, Owner, Reminder} from '~core/models';
import {DefaultNotificationService, ReminderService, StorageService} from '~core/services';
import {ReminderStatusTranslationsService} from '~shared/translations';
import {FormFieldImageUploadComponent} from '~shared/components/form-fields/form-field-image-upload/form-field-image-upload.component';
import {isFutureDateDirective} from '~shared/directives/form-validators/is-future-date.directive';

/**
 * Handles reminders
 */
@UntilDestroy()
@Component({
  selector: 'app-reminder',
  templateUrl: 'reminder.component.html',
  styleUrls: ['../../../customer-overview.component.scss']
})
export class ReminderComponent implements OnInit, OnDestroy {
  @ViewChild(FormFieldImageUploadComponent) imageUpload: FormFieldImageUploadComponent;
  public createReminderForm: UntypedFormGroup;
  public spinnerName: string = 'reminderCreate';
  public dateToday: string;
  public selectedClub: Club;
  public authenticatedUser: AuthenticatedUser;
  public possibleOwnersList: Array<Owner> = [];
  public pageLoading: boolean;
  public spinnerPageLoadName: string = 'editReminderLoading';
  @Input() public customer: Customer;

  constructor(
    private notificationService: DefaultNotificationService,
    private reminderService: ReminderService,
    private formBuilder: UntypedFormBuilder,
    private spinnerService: SpinnerService,
    private storageService: StorageService,
    private datePipe: DatePipe,
    public reminderTranslations: ReminderStatusTranslationsService,
    public buttonsTranslations: ButtonsTranslations,
    public flTranslations: FormLabelTranslationService,
    public customerOverviewService: CustomerOverViewService
  ) {
    this.storageService.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe((data: Club) => this.selectedClub = data);
    this.storageService.castAuthenticatedUser.pipe(untilDestroyed(this))
      .subscribe((data: AuthenticatedUser) => this.authenticatedUser = data);
  }

  public ngOnDestroy(): void {
  }

  public ngOnInit(): any {
    this.fillReminderForm();
    this.loadPossibleOwners();
  }

  /**
   * Create the contact form.
   */
  private fillReminderForm(): void {
    this.dateToday = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    this.createReminderForm = this.formBuilder.group({
      text: ['', [
        Validators.required,
        Validators.maxLength(1000),
        noWhitespaceCharacters
      ]],
      deadline: [this.dateToday, [
        Validators.required,
        isFutureDateDirective
      ]],
      status: ['open', [
        Validators.required
      ]],
      owner: [this.authenticatedUser.id, [
        Validators.required
      ]],
      customer: [`api/customers/${this.customer.id}`],
      attachment: null
    });
  }

  /**
   * Load all the possible owners that the user can assign
   */
  private loadPossibleOwners(): void {
    this.pageLoading = true;
    this.reminderService.getPossibleReminderOwners(this.selectedClub.id).pipe(first())
      .subscribe((data: Array<Owner>) => {
        this.possibleOwnersList = data;
        this.spinnerService.hide(this.spinnerPageLoadName);
      });
  }

  /**
   * Create the new reminder.
   */
  public createReminder(): void {
    if (this.createReminderForm.valid) {
      this.spinnerService.show(this.spinnerName);
      this.createReminderForm.controls['owner'].setValue(`api/users/${this.createReminderForm.controls['owner'].value}`);

      if (this.imageUpload.fileToUpload) {
        const formData: FormData = new FormData();
        formData.append('file', this.imageUpload.fileToUpload);
        this.submitAttachmentAndReminder(formData);
      } else {
        this.submitReminder();
      }
    }
  }

  /**
   * Verifies that a file is of type .gif, .jpg, .png.
   */
  public fileChange(file): void {
    if (!this.isAllowedSize(file)) {
      this.notificationService.showNotification({
        type: NotificationType.ALERT,
        message: this.reminderTranslations.i18nErrorFileSizeTooLarge
      });
    }
  }

  /**
   * Is provided file size below 5MB.
   */
  private isAllowedSize(file: File): boolean {
    return file.size < 5000000;
  }

  /**
   * Submits the reminder form
   */
  private submitReminder(): void {
    this.reminderService.create(this.createReminderForm.value).pipe(untilDestroyed(this))
      .subscribe({
        next: (reminder: Reminder) => {
          this.spinnerService.hide(this.spinnerName);
          this.reminderService.addToReminderList(reminder);
          this.notificationService.showNotification({
            type: NotificationType.SUCCESS,
            message: this.reminderTranslations.i18nSuccessReminderCreate
          });
          this.customerOverviewService.toggleAllModal(false);
        },
        error: (error: Error) => {
          this.spinnerService.hide(this.spinnerName);
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.reminderTranslations.i18nErrorReminderCreate
          });
          this.spinnerService.hide(this.spinnerName);
        }
      });
  }

  /**
   * Submits the attachment first and then the reminder form
   */
  private submitAttachmentAndReminder(formData: any): void {
    this.reminderService.uploadAttachment(formData).pipe(first())
      .subscribe({
        next: (res: any) => {
          this.createReminderForm.patchValue({attachment: `api/media/${res.id}`});
          this.submitReminder();
        },
        error: (err: Error) => {
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.reminderTranslations.i18nErrorCannotUploadAttachment
          });
          this.spinnerService.hide(this.spinnerName);
        }
      });
  }

}
